import * as React from 'react';

import { useQuery } from '@apollo/client';

import PartnerListContext from 'bundles/browse-common/contexts/PartnerListContext';
import { useSearchFacet } from 'bundles/browse-common/hooks/useSearchFacet';
import BrowseAllPartnersQuery from 'bundles/browse-common/queries/BrowseAllPartners.graphql';
import type {
  BrowseAllPartnersQuery as BrowseAllPartnersQueryReponse,
  BrowseAllPartnersQueryVariables,
  BrowseAllPartners_PartnerFragment as PartnerFragment,
} from 'bundles/browse-common/queries/__generated__/BrowseAllPartners';
import type { PartnerData } from 'bundles/browse-common/utils/experimentUtils';

type Props = {
  children: React.ReactNode;
};

const useQueryAllPartners = (skip: boolean) => {
  const { data, loading } = useQuery<BrowseAllPartnersQueryReponse, BrowseAllPartnersQueryVariables>(
    BrowseAllPartnersQuery,
    { skip, context: { clientName: 'gatewayGql' } }
  );
  return { data, loading };
};

const SkillValuePropProvider = ({ children }: Props) => {
  const { facet: partnersFacet, isLoading: isLoadingSearch } = useSearchFacet('partners');
  const { data, loading: isLoadingAllPartners } = useQueryAllPartners(!isLoadingSearch && !partnersFacet);

  const allPartnersMap = data?.Partner?.queryAll?.elements.reduce((acc, partner) => {
    acc[partner.name] = partner;
    return acc;
  }, {} as Record<string, PartnerFragment>);

  const partners = partnersFacet?.valuesAndCounts
    // Take the top 20 partners with the highest count in case there are some
    // that don't have a matching result in the allPartnersMap.
    // Only 8 will be displayed on the page.
    .slice(0, 20)
    .reduce((acc, partnerFromFacet) => {
      const partner = allPartnersMap?.[partnerFromFacet.value];

      if (partner && (partner.rectangularLogo || partner.logo || partner.squareLogo)) {
        acc.push({
          relationSlug: partner.slug,
          partnerName: partner.name,
          imageAltText: partner.name,
          image: { url: partner.rectangularLogo || partner.logo || partner.squareLogo || '' },
        });
      }

      return acc;
    }, [] as PartnerData[]);

  return (
    <PartnerListContext.Provider
      value={{
        partners,
        loading: isLoadingSearch || isLoadingAllPartners,
      }}
    >
      {children}
    </PartnerListContext.Provider>
  );
};

export default SkillValuePropProvider;
